import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import MarketingNav from "../components/MarketingNav";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";
import Mascot from "../components/atoms/Mascot";

export default function About() {
  return (
    <div style={{ background: "var(--paper)", minHeight: "100vh" }}>
      <MarketingNav />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "80px 24px 48px" }}>
        <Chip tone="gold">About</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 64px)", lineHeight: 1.05, fontWeight: 400, margin: "16px 0 18px", letterSpacing: "-0.025em" }}>
          A speakeasy for English, <em style={{ color: "var(--gold-500)" }}>not a karaoke bar.</em>
        </h1>
        <p style={lede}>
          The internet has plenty of bright, gamified English apps. They work for kids and casual learners. They don't work for the adult who needs to pass IELTS in eight weeks, or the engineer who hires up but freezes in the interview.
        </p>
        <p style={lede}>
          We built SpeakEasy for them. Quieter palette, longer attention spans, real coaching. The product takes you seriously because the work is serious.
        </p>
      </section>

      {/* Mission band */}
      <section style={{ background: "var(--ink-900)", color: "var(--paper)", padding: "64px 0" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 40, alignItems: "center" }}>
          <div className="hidden md:flex" style={{ justifyContent: "center" }}>
            <Mascot size={200} mood="listening" />
          </div>
          <div>
            <span className="eyebrow" style={{ color: "var(--gold-300)" }}>Our mission</span>
            <h2 className="font-display" style={{ fontSize: 38, fontWeight: 400, lineHeight: 1.15, margin: "8px 0 16px", color: "var(--paper)" }}>
              Make confident English <em style={{ color: "var(--gold-300)" }}>practical</em>, not aspirational.
            </h2>
            <p style={{ fontSize: 16, color: "rgba(251,246,232,0.78)", lineHeight: 1.65, margin: 0, maxWidth: "52ch" }}>
              We don't want you to memorize 50 lessons. We want you to walk into your next interview, your IELTS sitting, your job orientation — and have the words ready.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "96px 24px 48px" }}>
        <Chip tone="gold">How it works</Chip>
        <h2 className="font-display" style={h2}>Three things, <em style={{ color: "var(--gold-500)" }}>repeated daily.</em></h2>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 24 }}>
          {[
            { n: "01", title: "Learn the rule.", body: "One grammar topic per day, broken open word by word with role colors and IPA." },
            { n: "02", title: "Speak it back.",  body: "Real-time speech recognition listens and scores. The mic pulses in rust while it does." },
            { n: "03", title: "Test yourself.",  body: "A short quiz. Score 50% or higher to unlock tomorrow. Wrong answers come with a sentence-long explanation." },
          ].map((s) => (
            <Card key={s.n} paperDeep>
              <div className="font-display" style={{ fontSize: 56, lineHeight: 1, color: "var(--gold-500)", fontStyle: "italic", marginBottom: 8 }}>{s.n}</div>
              <h3 className="font-display" style={{ fontSize: 24, fontWeight: 400, margin: "0 0 8px" }}>{s.title}</h3>
              <p style={{ color: "var(--fg-2)", fontSize: 14, lineHeight: 1.55, margin: 0 }}>{s.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* What you'll learn */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "32px 24px 96px" }}>
        <Chip tone="gold">Curriculum</Chip>
        <h2 className="font-display" style={h2}>Fifty lessons, <em style={{ color: "var(--gold-500)" }}>four levels.</em></h2>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 24 }}>
          {[
            { spine: "var(--sage)",     name: "Beginner",     count: "12 lessons", topics: ["SVO structure", "Articles & pronouns", "Present tense"] },
            { spine: "var(--gold-500)", name: "Intermediate", count: "16 lessons", topics: ["All tenses", "Modals", "Comparatives"] },
            { spine: "var(--rust)",     name: "Advanced",     count: "14 lessons", topics: ["Phrasal verbs", "Conditionals", "Reported speech"] },
            { spine: "var(--ink-800)",  name: "IELTS",        count: "8 lessons",  topics: ["Speaking parts", "Writing tasks", "Reading + listening"] },
          ].map((l) => (
            <Card key={l.name} paperDeep spineColor={l.spine} style={{ paddingTop: 28 }}>
              <h3 className="font-display" style={{ fontSize: 22, fontWeight: 400, margin: "0 0 4px" }}>{l.name}</h3>
              <div className="font-display" style={{ fontStyle: "italic", color: "var(--fg-3)", fontSize: 14, marginBottom: 10 }}>{l.count}</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.7 }}>
                {l.topics.map((t) => <li key={t}>· {t}</li>)}
              </ul>
            </Card>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ background: "var(--paper-deep)", padding: "64px 0", textAlign: "center" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display" style={{ fontSize: 36, lineHeight: 1.15, fontWeight: 400, margin: "0 auto 14px", maxWidth: "20ch" }}>
            Twelve free lessons. <em style={{ color: "var(--gold-500)" }}>Start anywhere.</em>
          </h2>
          <p style={{ color: "var(--fg-2)", fontSize: 16, maxWidth: "48ch", margin: "0 auto 24px" }}>
            Take a quick placement test. We'll start you on Day 1, Day 8, or Day 12 — wherever your level lands.
          </p>
          <Link to="/onboarding" style={{ textDecoration: "none" }}>
            <Button variant="primary" size="lg" iconRight={<ArrowRight size={16} strokeWidth={1.8} />}>Start free</Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: "var(--ink-900)", color: "var(--paper)", padding: "32px 0" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ fontSize: 13, color: "rgba(251,246,232,0.55)", textAlign: "center" }}>
          © {new Date().getFullYear()} SpeakEasy by K2TechInfo. Made warm in India.
        </div>
      </footer>
    </div>
  );
}

const lede = { fontSize: 18, lineHeight: 1.65, color: "var(--fg-2)", maxWidth: "60ch", margin: "0 0 18px" };
const h2 = { fontSize: 38, lineHeight: 1.1, fontWeight: 400, letterSpacing: "-0.02em", margin: "12px 0 0" };
